import { getImageFromClippingList } from "@/shared/functions/getImagesFunctions";

const Ldjson = ({ props, summary = '', url, related_image_url = '', related_image_caption = '', authors, sections, sectionUrl, isLiveBlogs = false }) => {

    let customBreadcrum;

    const extractTextFromBody = (body) => {
        if (!Array.isArray(body)) return '';

        return body
            .filter((item) => item?.type === 'text' && typeof item?.value === 'string')
            .map((item) => item.value)
            .join(' ');
    };

    const extractImagesFromBody = (body) => {
        if (!Array.isArray(body)) return [];

        const images = [];
        body.forEach(item => {
            if (item?.type === 'image') {
                const clippings = item?.value?.clippings || [];
                const imgObject = getImageFromClippingList(clippings, '3_2_870x580');
                if (imgObject && imgObject.imgUrl && imgObject.imgUrl !== '#') {
                    const url = imgObject.domain + imgObject.imgUrl;
                    images.push({
                        "@type": "ImageObject",
                        "url": url,
                        "height": 580,
                        "width": 870,
                        "caption": item?.epigraphe || ''
                    });
                }
            }
        });
        return images;
    };

    const parseDuration = (dur = '') => {
        // Convierte "mm:ss" o "hh:mm:ss" a formato ISO 8601 (PT#H#M#S)
        const parts = dur.split(':').map(Number);
        if (parts.length === 2) {
            const [m, s] = parts;
            return `PT${m > 0 ? m + 'M' : ''}${s}S`;
        }
        if (parts.length === 3) {
            const [h, m, s] = parts;
            return `PT${h > 0 ? h + 'H' : ''}${m > 0 ? m + 'M' : ''}${s}S`;
        }
        return undefined;
    };

    const extractVideosFromBody = (body, articleTitle, articleSummary, uploadDate, fallbackThumbnailUrl) => {
        if (!Array.isArray(body)) return [];

        const videos = [];
        body.forEach(item => {
            if (item?.type === 'video' && item?.value) {
                const { thumbnail, videoFiles, entityId, name, description, duration, url: videoPageUrl } = item.value;
                const videoUrl = videoFiles?.mp4 || videoFiles?.m3u8 || '';
                if (videoUrl) {
                    const jwPlayerThumbnail = entityId ? `https://cdn.jwplayer.com/v2/media/${entityId}/poster.jpg?width=720` : '';
                    const defaultPlaceholder = 'https://img-ellitoral.lilax.io/collections/utiles-diseno/generica-redes-1___6273ea8aae16950017079e88.jpg';
                    const rawThumbnail = thumbnail || jwPlayerThumbnail || fallbackThumbnailUrl || defaultPlaceholder;
                    const videoThumbnail = rawThumbnail.replace(/width=\d+/, 'width=720');
                    const videoName = `[Video] ${name || articleTitle}`;
                    const videoDescription = stripHtml(description) || articleSummary || articleTitle;
                    const videoEntry = {
                        "@type": "VideoObject",
                        "name": videoName,
                        "description": videoDescription,
                        "thumbnailUrl": [videoThumbnail],
                        "uploadDate": uploadDate,
                        "contentUrl": videoUrl
                    };
                    const jwPlayerId = process.env.NEXT_PUBLIC_LOOP_JWPLAYER_ID?.split('/libraries/')[1]?.replace('.js', '');
                    const absoluteVideoPageUrl = videoPageUrl ? `${process.env.NEXT_PUBLIC_DOMAIN_URL}${videoPageUrl}` : null;
                    const videoViewingUrl = absoluteVideoPageUrl || (entityId && jwPlayerId ? `https://cdn.jwplayer.com/players/${entityId}-${jwPlayerId}.html` : null);
                    if (videoViewingUrl) videoEntry.url = videoViewingUrl;
                    if (entityId && jwPlayerId) videoEntry.embedUrl = `https://cdn.jwplayer.com/players/${entityId}-${jwPlayerId}.html`;
                    const isoDuration = parseDuration(duration);
                    if (isoDuration) videoEntry.duration = isoDuration;
                    videos.push(videoEntry);
                }
            }
        });
        return videos;
    };

    const stripHtml = (html = '') => {
        return html
            .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
            .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
            .replace(/<[^>]+>/g, ' ')
            .replace(/&nbsp;/gi, ' ')
            .replace(/&amp;/gi, '&')
            .replace(/&quot;/gi, '"')
            .replace(/&#39;/gi, "'")
            .replace(/&lt;/gi, '<')
            .replace(/&gt;/gi, '>')
            .replace(/\s+/g, ' ')
            .trim();
    };

    if (sections && sections.length > 0) {
        customBreadcrum = {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": []
        };

        const breadList = sections.map((section, index) => {
            return {
                "@type": "ListItem",
                "position": index + 1,
                "name": section.name?.toUpperCase(),
                "item": `${process.env.NEXT_PUBLIC_DOMAIN_URL}${sectionUrl}`
            };
        });

        customBreadcrum.itemListElement = breadList;
    }

    let data = [];

    const rawArticleBody = extractTextFromBody(props.body);
    const articleBodylimpio = stripHtml(rawArticleBody);
    const wordCount = articleBodylimpio
        ? articleBodylimpio.split(/\s+/).filter(Boolean).length
        : 0;
    const articleBody = articleBodylimpio.slice(0, 5000);

    const bodyImages = extractImagesFromBody(props.body);
    const images = [];
    if (related_image_url) {
        const firstImage = {
            "@type": "ImageObject",
            "url": related_image_url,
            "height": 655,
            "width": 1300,
            "datePublished": props.publishedDate,
            "dateModified": props.modificatedAt
        };
        if (related_image_caption) firstImage.caption = related_image_caption;
        images.push(firstImage);
    }
    bodyImages.forEach(img => {
        if (img.url !== related_image_url) {
            images.push(img);
        }
    });

    const bodyVideos = extractVideosFromBody(props.body, props.title, summary, props.publishedDate, related_image_url);

    const LdArticle = {
        "@context": "https://schema.org",
        "@type": "NewsArticle",
        "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": url
        },
        "headline": props.title,
        "alternativeHeadline": props.subtitle,
        "url": url,
        "image": images,
        "dateCreated": props.publishedDate,
        "datePublished": props.publishedDate,
        "dateModified": props.modificatedAt,
        "description": summary,
        "isAccessibleForFree": true,
        "citation": { "@type": "CreativeWork", "url": url },
        "articleBody": articleBody,
        "wordCount": wordCount,
        "inLanguage": "es-AR",
        "contentLocation": [{ "@type": "Country", "name": "Argentina" }],
        "speakable": {
            "@type": "SpeakableSpecification",
            "xPath": [
                "/html/head/title",
                "/html/head/meta[@name='description']/@content"
            ],
            "cssSelector": [".text-content p"]
        },
        "articleSection": props.sections.map((e) => e.name),
        "author": (authors && authors.length > 0)
            ? authors.map((author) => ({
                "@type": "Person",
                "name": author.name,
                "url": 'https://www.ellitoral.com/autor/' + author.slug,
                "sameAs": 'https://www.ellitoral.com/autor/' + author.slug,
                ...(author.imageUrl && { "image": author.imageUrl }),
                ...(author.email && { "email": author.email })
            }))
            : [{
                "@type": "Organization",
                "name": "El Litoral",
                "url": "https://www.ellitoral.com",
            }],
        "keywords": props.tags.map((e) => e.name),
        "about": props.tags.map((e) => ({ "@type": "Thing", "name": e.name })),
        "isPartOf": {
            "@type": ["CreativeWork", "Product"],
            "name": "El Litoral",
            "url": "https://www.ellitoral.com"
        },
        "hasPart": {
            "@type": "WebPageElement",
            "isAccessibleForFree": true,
            "cssSelector": ".article"
        },
        "copyrightHolder": { "@type": "Organization", "name": "El Litoral" },
        "copyrightYear": props.publishedDate ? props.publishedDate.split('-')[0] : new Date().getFullYear().toString(),
        "publisher": {
            "@type": "Organization",
            "name": "El Litoral",
            "url": "https://www.ellitoral.com",
            "logo": {
                "@type": "ImageObject",
                "url": `${process.env.NEXT_PUBLIC_DOMAIN_URL}/img/header_logoellitoral.svg`,
                "width": 439,
                "height": 60
            },
            "sameAs": [
                "https://www.facebook.com/ellitoralcom",
                "https://instagram.com/ellitoral",
                "https://twitter.com/ellitoral",
                "https://www.youtube.com/c/EllitoralArgentina"
            ]
        },
    };

    if (bodyVideos.length > 0) {
        LdArticle.video = bodyVideos.length === 1 ? bodyVideos[0] : bodyVideos;
    }

    data.push(LdArticle);

    if (customBreadcrum) {
        data.push(customBreadcrum);
    }

    const LdOrganization = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "legalName": "El Litoral",
        "name": "El Litoral",
        "description": "Diario El Litoral, el medio de comunicación de mayor trayectoria de la provincia de Santa Fe. Noticias de Santa Fe, Argentina y el mundo actualizadas las 24 horas.",
        "url": "https://www.ellitoral.com",
        "logo": `${process.env.NEXT_PUBLIC_DOMAIN_URL}/img/header_logoellitoral.svg`,
        "foundingDate": "1918",
        "address": {
            "@type": "PostalAddress",
            "streetAddress": "Elizabeth Guillamón de Agnellini 2670",
            "addressLocality": "Santa Fe",
            "addressRegion": "Santa Fe",
            "postalCode": "3000",
            "addressCountry": "AR"
        },
        "sameAs": [
            "https://www.facebook.com/ellitoralcom",
            "https://instagram.com/ellitoral",
            "https://twitter.com/ellitoral",
            "https://www.youtube.com/c/EllitoralArgentina"
        ]
    };
    data.push(LdOrganization);

    const LdNewsMediaOrg = {
        "@context": "https://schema.org",
        "@type": "NewsMediaOrganization",
        "name": "El Litoral",
        "description": "Diario El Litoral, el medio de comunicación de mayor trayectoria de la provincia de Santa Fe. Noticias de Santa Fe, Argentina y el mundo actualizadas las 24 horas.",
        "url": "https://www.ellitoral.com",
        "masthead": "https://www.ellitoral.com/quienes-somos",
        "publishingPrinciples": "https://www.ellitoral.com/quienes-somos",
        "ethicsPolicy": "https://www.ellitoral.com/politicas-privacidad-facebook-news",
        "correctionsPolicy": "https://www.ellitoral.com/politicas-privacidad-facebook-news",
        "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+543426305344",
            "contactType": "customer service",
            "email": "contenidos@ellitoral.com"
        }
    };
    data.push(LdNewsMediaOrg);

    if (related_image_url) {
        const LdMainImage = {
            "@context": "https://schema.org",
            "@type": "ImageObject",
            "contentUrl": related_image_url,
            "height": 655,
            "width": 1300,
            "datePublished": props.publishedDate ? props.publishedDate.split('T')[0] : new Date().toISOString().split('T')[0]
        };
        if (related_image_caption) {
            LdMainImage.name = related_image_caption;
            LdMainImage.description = related_image_caption;
        }
        data.push(LdMainImage);
    }

    return (
        <>
            {
                !isLiveBlogs && (
                    <script
                        type="application/ld+json"
                        data-script="beforeInteractive" 
                        dangerouslySetInnerHTML={{
                            __html: JSON.stringify(data)
                        }}
                    />
                )
            }
        </>
    );
}

export default Ldjson;